<script setup>
import {computed, onMounted, onUnmounted, reactive, ref, watch} from "vue";
import {useStore} from "vuex";
import {useRoute} from "vue-router";
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const store = useStore()
const route = useRoute()

onMounted(async ()=>{
  await fetchAttendances()
  await fetchEvaluation()
  store.dispatch('WebSocketSeminar/connect', pins.value)
})

onUnmounted(()=>{
  clearReconnectInterval()
  store.dispatch('WebSocketSeminar/disconnect')
  store.dispatch('SeminarAttendance/clear')
  store.dispatch('SeminarEvaluation/clear')
})

const loading = reactive({attendance: false, evaluation: false})
const error = reactive({attendance: '', evaluation: ''})
const search = ref('')
const reconnectInterval = ref(null)

const seminarId = computed(() => route.params.seminarId)
const connectionError = computed(()=> store.getters['WebSocketSeminar/connectionError'])
const pins = computed(() => attEvalPins())

watch(pins, async (currentPins, previousPins) => {
  if(currentPins.join(',') === previousPins.join(',')) return
  store.dispatch('WebSocketSeminar/connect', currentPins)
  await fetchAttendances()
  await fetchEvaluation()
})

watch(connectionError, async (connError) => {
  if(connError === true){
    if(reconnectInterval.value) return
    console.info('Reconnecting seminar websocket every 3 seconds...')
    reconnectInterval.value = setInterval(()=>{
      store.dispatch('WebSocketSeminar/connect', pins.value)
    }, 3000)
  } else {
    if(reconnectInterval.value){
      clearReconnectInterval()
      await fetchAttendances()
      await fetchEvaluation()
    }
  }
})

function attEvalPins(){
  const activities = [store.getters['Activities/activity'], store.getters['Activities/separateActivity']]
  return activities
      .filter(a => a.pin && (a.types.includes('ATT') || a.types.includes('EVAL')))
      .map(a => a.pin)
}

function clearReconnectInterval(){
  if(reconnectInterval.value){
    clearInterval(reconnectInterval.value)
    reconnectInterval.value = null
  }
}

async function fetchData(action, loadingKey, errorKey) {
  loading[loadingKey] = true;
  error[errorKey] = '';
  try {
    await store.dispatch(action, seminarId.value);
  } catch (e) {
    console.log('error ', e);
    error[errorKey] = t('gamification.error');
  } finally {
    loading[loadingKey] = false;
  }
}

async function fetchAttendances(){
  await fetchData('SeminarAttendance/fetchBySeminarId', 'attendance', 'attendance');
}

async function fetchEvaluation(){
  await fetchData('SeminarEvaluation/fetchBySeminarId', 'evaluation', 'evaluation');
}

const attendancesHeaders = [
    {title: 'Login', key: 'login', width: "50%",},
    {title: t('gamification.attendance_date'), key: 'createdAt', width: "50%", sortable: false}
]
const evaluationHeaders = [
  {title: t('gamification.star_count'), key: 'rating', width: "30%"},
  {title: t('gamification.rating'), key: 'text', width: "70%",}
]

function formatISODate(isoString) {
  const date = new Date(isoString);

  const options = {
    hour: "2-digit",
    minute: "2-digit",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  };
  return date.toLocaleString("cs-CZ", options);
}
</script>

<template>
  <div class="pt-5 px-5">
    <div class="d-flex justify-space-between align-center">
      <h2>  {{ $t('gamification.attendance') }} </h2>
      <v-btn
          @click="fetchAttendances"
          prepend-icon="refresh"
          class="mt-5"
          rounded
          color="primary"
          variant="outlined"
      >
        {{ $t('gamification.update') }}
      </v-btn>
    </div>
    <v-text-field
        v-model="search"
        prepend-icon="search"
        variant="underlined"
        :placeholder="$t('gamification.search_user')"
        class="w-50 pt-2"
    />
    <v-data-table
        :search="search"
        :loading="loading.attendance"
        :headers="attendancesHeaders"
        :items="store.getters['SeminarAttendance/getAttendances']"
        :loading-text="$t('gamification.loading')"
        :no-data-text="$t('gamification.no_data')"
    >
      <template v-slot:item.createdAt="{ value }">
        {{formatISODate(value)}}
      </template>
    </v-data-table>

    <div class="d-flex justify-space-between align-center">
      <div class="d-flex ga-2 align-end">
        <h2>{{ $t('gamification.evaluation') }}</h2>
        <v-chip variant="flat" label append-icon="star" color="grey-lighten-4">
          {{ store.getters['SeminarEvaluation/getRatingAverage'] }}
        </v-chip>
        <v-chip variant="flat" label color="grey-lighten-4">
          {{ store.getters['SeminarEvaluation/getEvaluation'].length }} {{ $t('gamification.users_genitive') }}
        </v-chip>
      </div>
      <v-btn
          @click="fetchEvaluation"
          prepend-icon="refresh"
          class="mt-5"
          rounded
          color="primary"
          variant="outlined"
      >
        {{ $t('gamification.update') }}
      </v-btn>
    </div>
    <v-data-table

        :loading="loading.evaluation"
        :headers="evaluationHeaders"
        :items="store.getters['SeminarEvaluation/getEvaluation']"
        :loading-text="$t('gamification.loading')"
        :no-data-text="$t('gamification.no_data')"
    >
      <template v-slot:item.rating="{ value }">
        <div class="d-flex ga-2">
          <v-icon
              v-for="_ in value"
              :key="_"
              icon="star"
              color="yellow-darken-1"
          />
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<style scoped>
.v-chip :deep(.v-chip__append .v-icon) {
  color: #fdd835;
}
</style>